import React, { useState, useRef, useEffect } from "react";
import { Button } from "./Button";
import { Mic, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { API_BASE } from "../config";
import { useAuth } from "./AuthProvider";

export default function VoiceAssistant() {
  const { user, handleLogout } = useAuth();

  const [isRecording, setIsRecording] = useState(false);
  const [response, setResponse] = useState(null);
  const [topic, setTopic] = useState("");
  const [loading, setLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [recordingTime, setRecordingTime] = useState("0:00");
  const [listeningText, setListeningText] = useState("");

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const timeIntervalRef = useRef(null);
  const recognitionRef = useRef(null);
  const barsRef = useRef([]);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const isRecordingRef = useRef(false);

  // Data URI for ultimate fallback (inline SVG avatar with initial)
  const getFallbackDataUri = (initial = "U") => {
    return `data:image/svg+xml;base64,${btoa(
      `<svg width="48" height="48" xmlns="http://www.w3.org/2000/svg">
        <circle cx="24" cy="24" r="24" fill="#6B7280" />
        <text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="white" font-family="Arial, sans-serif" font-size="20" font-weight="bold">${initial}</text>
      </svg>`
    )}`;
  };

  // --- helpers ---
  const toEmbedUrl = (url) => {
    try {
      const u = new URL(url);
      const id = u.searchParams.get("v");
      return id
        ? `https://www.youtube.com/embed/${id}`
        : url.replace("watch?v=", "embed/").split("&")[0];
    } catch {
      return url.replace("watch?v=", "embed/").split("&")[0];
    }
  };

  const userInitial = user?.name?.charAt(0)?.toUpperCase() || "U";
  const fallbackDataUri = getFallbackDataUri(userInitial);

  // --- recording ---
  const startRecording = async () => {
    try {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        setResponse(
          "⚠️ Your browser does not support the Web Speech API. Please use a modern browser like Chrome or Edge."
        );
        return;
      }

      // ask for mic
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      const recognition = new SpeechRecognition();
      recognition.interimResults = true;
      recognition.lang = "en-US";
      recognitionRef.current = recognition;

      recognition.onresult = (event) => {
        let interimTranscript = "";
        for (let i = event.resultIndex; i < event.results.length; ++i) {
          interimTranscript += event.results[i][0].transcript;
        }
        setListeningText(interimTranscript);
      };

      recognition.onend = () => {
        setListeningText("");
      };

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        // stop recording UI timers
        clearInterval(timeIntervalRef.current);

        // send blob if present
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/wav" });
        if (audioBlob.size > 1000) {
          await sendToBackend(audioBlob);
        } else if (!response) {
          setResponse("⚠️ No voice detected. Please try again later.");
        }

        setRecordingTime("0:00");
        setListeningText("");

        // close audio context if open
        if (audioContextRef.current) {
          try {
            audioContextRef.current.close();
          } catch (err) {
            // ignore
          }
          audioContextRef.current = null;
          analyserRef.current = null;
        }

        // stop any pending animation frame
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
      };

      // --- Audio visualization setup (Web Audio API) ---
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioCtx();
      audioContextRef.current = audioContext;

      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048; // higher for smoother time-domain waveform
      analyserRef.current = analyser;
      source.connect(analyser);

      const bufferLength = analyser.fftSize;
      const dataArray = new Uint8Array(bufferLength);

      // start the visual loop
      const updateBars = () => {
        if (!analyserRef.current) return;

        analyserRef.current.getByteTimeDomainData(dataArray);

        // Compute RMS (loudness)
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          const v = (dataArray[i] - 128) / 128; // -1..1
          sum += v * v;
        }
        const rms = Math.sqrt(sum / dataArray.length); // 0..1-ish
        // scale loudness to pixel amplitude with increased range
        const loudness = Math.min(rms * 150, 60); // Increased multiplier (150) and max (60)

        // time for traveling wave
        const time = performance.now() / 120; // Faster wave speed for more movement

        // update each bar with sine wave modulated by loudness and varying base height
        barsRef.current.forEach((bar, i) => {
          if (!bar) return;
          const phase = i * 0.25; // phase offset between bars
          const wave = Math.sin(time + phase); // -1 .. 1
          // Vary base height (10px to 30px) and amplify oscillation
          const baseHeight = 10 + (i % 5) * 4; // Staggered base heights (10, 14, 18, 22, 26)
          const scale = 0.2 + (wave * loudness) / 10; // Amplified scale (0.2 to 6.2)
          // apply transform for bidirectional movement with varied lengths
          bar.style.transform = `scaleY(${scale})`;
          bar.style.height = `${baseHeight}px`; // Set initial varied height
          // adjust opacity based on absolute scale
          const opacity = Math.max(0.3, 0.5 + Math.abs(scale - 1) * 0.5);
          bar.style.opacity = `${opacity}`;
        });

        if (isRecordingRef.current) {
          animationFrameRef.current = requestAnimationFrame(updateBars);
        }
      };

      // Start everything
      mediaRecorder.start();
      try {
        recognition.start();
      } catch (err) {
        // some browsers throw if recognition already running; ignore
      }

      // set flags and start loop
      setIsRecording(true);
      isRecordingRef.current = true;
      setResponse(null);
      setLoading(true);

      // start visualizer
      animationFrameRef.current = requestAnimationFrame(updateBars);

      // recording timer UI
      let timeSec = 0;
      timeIntervalRef.current = setInterval(() => {
        timeSec++;
        const minutes = Math.floor(timeSec / 60);
        const seconds = timeSec % 60;
        setRecordingTime(`${minutes}:${seconds.toString().padStart(2, "0")}`);
      }, 1000);

      // auto-stop after a duration (you can change or remove)
      timerRef.current = setTimeout(() => {
        stopRecording();
      }, 5000);
    } catch (err) {
      console.error("Microphone access or setup failed", err);
      setResponse("❌ Microphone access denied or error starting recorder.");
      setLoading(false);
      setIsRecording(false);
      isRecordingRef.current = false;
    }
  };

  const stopRecording = () => {
    // stop recorder
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      try {
        mediaRecorderRef.current.stop();
      } catch (err) {
        // ignore
      }
    }

    // stop recognition
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (err) {
        // ignore
      }
    }

    // stop animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // close audio context
    if (audioContextRef.current) {
      try {
        audioContextRef.current.close();
      } catch (err) {
        // ignore
      }
      audioContextRef.current = null;
      analyserRef.current = null;
    }

    clearTimeout(timerRef.current);
    clearInterval(timeIntervalRef.current);
    setIsRecording(false);
    isRecordingRef.current = false;
    setLoading(false);
    setRecordingTime("0:00");
  };

  // --- API call ---
  const sendToBackend = async (audioBlob) => {
    setLoading(true);
    setResponse(null);
    const formData = new FormData();
    formData.append("audio_file", audioBlob, "voice.wav");

    try {
      // small artificial delay to show loading state nicely
      await new Promise((resolve) => setTimeout(resolve, 500));
      const res = await fetch(`${API_BASE}/api/listen`, {
        method: "POST",
        body: formData,
      });

      const data = await res.json();
      const result = data?.response?.result;
      const fetchedTopic = data?.response?.topic || "";

      if (Array.isArray(result)) {
        setResponse(result);
        setTopic(fetchedTopic);
      } else if (typeof result === "object" && result !== null && result.type === "general") {
        setResponse(result.response);
        setTopic(fetchedTopic);
      } else {
        setResponse("🤔 No response from server, please try again later.");
      }
    } catch (err) {
      console.error(err);
      setResponse("🚨 Error: Unable to reach server");
    } finally {
      setLoading(false);
    }
  };

  // --- Effect to handle user image loading ---
  useEffect(() => {
    setImageLoading(true);
    if (!user || !user.name) {
      console.warn("User or name is unavailable:", user);
      setImageLoading(false);
      return;
    }
    let imageUrl = user.picture;
    
    // If no picture or invalid URL, use placeholder
    if (!imageUrl || !imageUrl.startsWith('http')) {
      imageUrl = "https://picsum.photos/48/48?random=1"; // Reliable placeholder
      console.log("Using placeholder image URL:", imageUrl);
    } else {
      // Use images.weserv.nl proxy for Google images
      imageUrl = `https://images.weserv.nl/?url=${encodeURIComponent(imageUrl)}&w=48&h=48&fit=cover`;
      console.log("Using proxied image URL:", imageUrl);
    }
    
    console.log("Attempting to load image:", imageUrl);
    const img = new Image();
    img.src = imageUrl;
    img.onload = () => {
      console.log("User image loaded successfully");
      setImageLoading(false);
    };
    img.onerror = (e) => { // Added 'e' as a parameter here
      console.error("Failed to load user image (falling back to data URI):", imageUrl);
      e.target.src = fallbackDataUri;
      setImageLoading(false);
    };
    // Timeout to prevent hanging
    const timeout = setTimeout(() => {
      console.warn("Image loading timed out, using data URI fallback:", imageUrl);
      setImageLoading(false);
    }, 5000);
    return () => clearTimeout(timeout);
  }, [user, fallbackDataUri]);

  // inject styles on mount and cleanup on unmount
  useEffect(() => {
    const styles = `
      :root {
        --neon-blue: #00ffcc;
        --neon-pink: #ff00ff;
        --neon-green: #00ff00;
        --neon-yellow: #ffff00;
        --neon-white: #e0e0e0;
      }
      .neon-text {
        text-shadow: 0 0 5px var(--neon-blue), 0 0 10px var(--neon-blue), 0 0 15px var(--neon-blue);
      }
      .wave-container {
        display: flex;
        justify-content: center;
        align-items: center; /* Center bars vertically for up/down oscillation */
        gap: 4px;
        height: 72px;
        padding: 6px 4px;
      }
      .wave-bar {
        width: 6px;
        height: 20px; /* Base height for bars (overridden by JS) */
        background: linear-gradient(180deg, rgba(0,255,204,0.9), rgba(0,200,255,0.8));
        border-radius: 4px;
        transform-origin: center; /* Scale from center for symmetric oscillation */
        transition: transform 0.06s linear, opacity 0.06s linear; /* Faster transition */
        will-change: transform, opacity;
        box-shadow: 0 0 8px rgba(0,255,204,0.12), inset 0 -6px 12px rgba(255,255,255,0.02);
      }
    `;
    const styleEl = document.createElement("style");
    styleEl.id = "voice-assistant-styles";
    styleEl.textContent = styles;
    document.head.appendChild(styleEl);
    return () => {
      if (document.head.contains(styleEl)) document.head.removeChild(styleEl);
    };
  }, []);

  // cleanup on unmount: stop audio and timers
  useEffect(() => {
    return () => {
      // stop any running recorder or audio
      try {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
          mediaRecorderRef.current.stop();
        }
      } catch (err) {}
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (err) {}
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current) {
        try {
          audioContextRef.current.close();
        } catch (err) {}
      }
      clearTimeout(timerRef.current);
      clearInterval(timeIntervalRef.current);
    };
  }, []);

  // --- UI ---
  const BAR_COUNT = 32;

  return (
    <div className="min-h-screen w-full bg-gradient-to-r from-cyan-900 via-purple-900 to-pink-900 p-6 flex flex-col items-center justify-center relative text-white font-sans">
      {/* User info (top-right) */}
      <div className="absolute top-6 right-6 flex items-center gap-3">
        <div className="relative">
          <motion.div
            whileHover={{ scale: 1.1 }}
            className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center overflow-hidden border-2 border-neon-blue transition-all duration-300"
          >
            {imageLoading ? (
              <Loader2 className="w-6 h-6 text-neon-blue animate-spin" />
            ) : user?.picture ? (
              <img
                src={`https://images.weserv.nl/?url=${encodeURIComponent(user.picture)}&w=48&h=48&fit=cover`}
                alt={user.name || "User"}
                className="w-full h-full object-cover"
                onError={(e) => {
                  console.error("Image failed to load in UI (using data URI fallback):", e.target.src);
                  e.target.src = fallbackDataUri;
                }}
              />
            ) : (
              <img
                src={fallbackDataUri}
                alt={user.name || "User"}
                className="w-full h-full object-cover"
              />
            )}
          </motion.div>
          <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-gray-900"></div>
        </div>
        <div className="text-right">
          <p className="text-sm font-semibold neon-text">{user?.name}</p>
          <button
            onClick={handleLogout}
            className="text-xs text-neon-green hover:text-white underline transition-colors"
          >
            Logout
          </button>
        </div>
      </div>

      {/* Header */}
      <motion.h1
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="text-5xl md:text-6xl font-extrabold text-white mb-8 text-center tracking-wide drop-shadow-[0_0_10px_rgba(255,255,255,0.7)]"
      >
        🤖 Tech Voice Assistant
      </motion.h1>

      {/* Instruction text */}
      <p className="text-lg md:text-xl text-center max-w-2xl mb-10 text-neon-white drop-shadow-[0_0_5px_rgba(255,255,255,0.5)]" style={{ color: "#84a1a0ff" }}>
        I can assist you with queries related to MAP, CDB and Profiler tools. Say your query after pressing the button.
      </p>

      {/* Mic button and wave animation container */}
      <div className="flex flex-col items-center gap-6">
        <motion.div
          className="relative"
          initial={{ scale: 1 }}
          whileHover={{ scale: 1.1 }}
          animate={{ boxShadow: isRecording ? "0 0 20px #ff00ff" : "0 0 10px #00ffcc" }}
          transition={{ duration: 0.3 }}
        >
          <Button
            onClick={isRecording ? stopRecording : startRecording}
            className={`rounded-full w-20 h-20 md:w-24 md:h-24 flex items-center justify-center bg-gradient-to-br from-neon-blue to-neon-pink border-4 border-neon-green hover:bg-opacity-90 transition-all duration-300 shadow-[0_0_15px_rgba(0,255,204,0.7)]`}
            aria-label="Start or stop recording"
          >
            <Mic className={`w-10 h-10 text-neon-green ${isRecording ? "hidden" : ""}`} />
          </Button>
          <motion.div
            className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-semibold text-neon-white bg-black/50 px-3 py-1 rounded-full"
            initial={{ opacity: 0 }}
            whileHover={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
          >
            Activate Voice
          </motion.div>
        </motion.div>

        {/* Wave animation and listening text container */}
        <AnimatePresence>
          {isRecording && (
            <motion.div
              key="listening-ui"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="flex flex-col items-center gap-4 mt-6"
            >
              <div className="wave-container">
                {Array.from({ length: BAR_COUNT }).map((_, i) => (
                  <div
                    key={i}
                    ref={(el) => (barsRef.current[i] = el)}
                    className="wave-bar"
                  />
                ))}
              </div>
              <p className="text-neon-white text-center font-medium text-lg whitespace-pre-wrap">
                {listeningText || "Listening..."}
              </p>
              <p className="text-neon-white/70 mt-2 text-sm">Recording Time: {recordingTime}</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Loading animation */}
      {loading && (
        <div className="flex justify-center items-center gap-3 text-neon-white font-semibold animate-pulse mt-8">
          <Loader2 className="w-6 h-6 animate-spin" /> Processing your command...
        </div>
      )}

      {/* Response area with added gap */}
      <div className="mt-12"> {/* Added margin-top for gap */}
        <AnimatePresence mode="wait">
          {response && (
            <motion.div
              key="response-display"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className={`w-full max-w-4xl bg-black/30 backdrop-blur-md rounded-xl ${
                Array.isArray(response) && response.length === 1 ? 'p-4' : 'p-6 mt-10'
              } border-2 border-neon-blue shadow-[0_0_20px_rgba(0,191,255,0.5)]`}
            >
              {Array.isArray(response) ? (
                <>
                  <p className="text-neon-white text-xl font-semibold text-center mb-6 tracking-wide">
                    Explore more about{" "}
                    <span className="text-neon-yellow">{topic || "this topic"}</span>{" "}
                    with these resources:
                  </p>
                  <div 
                    className={`grid gap-6 place-items-center ${
                      response.length === 1 
                        ? 'grid-cols-1 max-w-xl mx-auto' 
                        : 'grid-cols-1 md:grid-cols-2'
                    }`}
                  >
                    {response.map((item, index) => {
                      const embed = toEmbedUrl(item.media_link || "");
                      return (
                        <motion.div
                          key={index}
                          whileHover={{
                            scale: 1.05,
                            boxShadow: "0 0 25px rgba(0, 255, 204, 0.6)",
                          }}
                          className="w-full max-w-[520px] bg-gradient-to-br from-purple-900/50 to-pink-900/50 rounded-lg p-4 border border-neon-green transition-all duration-300"
                        >
                          <div className="rounded-lg overflow-hidden shadow-lg">
                            <iframe
                              className="w-full aspect-video rounded-lg"
                              src={embed}
                              title={item.title}
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                          </div>
                          <a
                            href={item.media_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block text-center text-neon-yellow font-medium mt-3 hover:underline"
                            title="Open on YouTube"
                          >
                            {item.title || `Video ${index + 1}`}
                          </a>
                        </motion.div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <p className="text-neon-white text-center font-medium text-lg whitespace-pre-wrap">
                  {response}
                </p>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}